import type { QueryClient } from '@tanstack/react-query'

import type { ReminderApiItem } from '@renderer/overlay/Sidepanel/hooks/reminder-mapping'
import { REMINDERS_QUERY_KEY } from '@renderer/overlay/Sidepanel/hooks/reminder-query-keys'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/staged-actions/types'
import { STAGED_ACTIONS_QUERY_KEY } from '@renderer/overlay/Sidepanel/hooks/staged-actions/utils'
import type { HistoryActivityInfiniteData } from '@renderer/overlay/Sidepanel/hooks/history-query-keys'
import type { RecentActivity } from '@renderer/overlay/Sidepanel/types'
import { rememberPreviewAcknowledgement } from './conversation-preview-acknowledgements'
import type { SidepanelChat, SidepanelChatApiItem } from './running-chat-selection'
import { FOR_YOU_CHATS_QUERY_KEY, HISTORY_ACTIVITY_QUERY_KEY } from '@renderer/stores/conversation-query-keys'

export type ConversationPreviewDisposition = 'seen' | 'dismissed'

interface ForYouChatsPage {
  data: SidepanelChatApiItem[]
  nextCursor?: string
}

export interface ForYouChatsInfiniteData {
  pages: ForYouChatsPage[]
  pageParams: unknown[]
}

export type ForYouChatsCacheData = SidepanelChatApiItem[] | ForYouChatsInfiniteData

function acknowledgeRows(
  rows: SidepanelChatApiItem[] | undefined,
  conversationId: string,
  revision: number,
): SidepanelChatApiItem[] {
  return (rows ?? []).map((row) =>
    row.id === conversationId
      ? { ...row, previewAcknowledgedRevision: Math.max(row.previewAcknowledgedRevision ?? 0, revision) }
      : row,
  )
}

function acknowledgeForYouData(
  data: ForYouChatsCacheData | undefined,
  conversationId: string,
  revision: number,
): ForYouChatsCacheData | undefined {
  if (!data) {
    return undefined
  }
  if (Array.isArray(data)) {
    return acknowledgeRows(data, conversationId, revision)
  }
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: acknowledgeRows(page.data, conversationId, revision),
    })),
  }
}

/**
 * Recents draws its dot off the same revisions, so an acknowledgement has to land in the history
 * feed too. Otherwise the row keeps its dot until that feed's next refetch.
 */
function acknowledgeHistoryData(
  data: HistoryActivityInfiniteData | undefined,
  conversationId: string,
  revision: number,
): HistoryActivityInfiniteData | undefined {
  if (!data) {
    return undefined
  }
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data.map((item) =>
        item.type === 'chat' && item.id === conversationId
          ? { ...item, previewAcknowledgedRevision: Math.max(item.previewAcknowledgedRevision ?? 0, revision) }
          : item,
      ),
    })),
  }
}

/**
 * The history row for a conversation, from whichever type filter has it cached. The History view
 * pages each filter separately, so a chat read under the Chats filter can be absent from the
 * unfiltered feed the sidepanel loads.
 */
export function findHistoryChatRow(queryClient: QueryClient, conversationId: string): RecentActivity | undefined {
  const caches = queryClient.getQueriesData<HistoryActivityInfiniteData>({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] })
  for (const [, data] of caches) {
    for (const page of data?.pages ?? []) {
      const item = page.data.find((candidate) => candidate.type === 'chat' && candidate.id === conversationId)
      if (item) return item
    }
  }
  return undefined
}

export function applyConversationPreviewAcknowledgement(
  queryClient: QueryClient,
  chat: SidepanelChat,
  disposition: ConversationPreviewDisposition,
): void {
  rememberPreviewAcknowledgement(chat.id, chat.previewRevision)
  queryClient.setQueryData<ForYouChatsCacheData>([FOR_YOU_CHATS_QUERY_KEY], (data) =>
    acknowledgeForYouData(data, chat.id, chat.previewRevision),
  )
  // The feed is cached per type filter ('all', 'chat', …), so this needs setQueriesData.
  queryClient.setQueriesData<HistoryActivityInfiniteData>({ queryKey: [HISTORY_ACTIVITY_QUERY_KEY] }, (data) =>
    acknowledgeHistoryData(data, chat.id, chat.previewRevision),
  )
  if (disposition !== 'dismissed') {
    return
  }

  const archivedAt = new Date().toISOString()
  const actionIds = new Set(chat.stagedActionIds)
  const reminderIds = new Set(chat.reminderIds)
  queryClient.setQueryData<StagedAction[]>([STAGED_ACTIONS_QUERY_KEY], (actions) =>
    (actions ?? []).map((action) => (actionIds.has(action.id) ? { ...action, archivedAt } : action)),
  )
  queryClient.setQueryData<ReminderApiItem[]>([REMINDERS_QUERY_KEY], (reminders) =>
    (reminders ?? []).map((reminder) =>
      reminderIds.has(reminder.id) ? { ...reminder, metadata: { ...reminder.metadata, dismissed: 'true' } } : reminder,
    ),
  )
}
